import { alpha, Box } from "@mui/system";
import { Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import {
  AutoFixHighOutlined,
  BugReportOutlined,
  HelpOutlineOutlined,
  SpeedOutlined,
  PaletteOutlined,
  MenuBookOutlined,
  SecurityOutlined,
} from "@mui/icons-material";

const CATEGORIES = [
  {
    value: "bug",
    label: "Bug",
    icon: BugReportOutlined,
    color: "error",
    description: "Algo está quebrado",
  },
  {
    value: "melhoria",
    label: "Melhoria",
    icon: AutoFixHighOutlined,
    color: "primary",
    description: "Nova funcionalidade",
  },
  {
    value: "ux",
    label: "Interface / UX",
    icon: PaletteOutlined,
    color: "secondary",
    description: "Visual ou usabilidade",
  },
  {
    value: "performance",
    label: "Performance",
    icon: SpeedOutlined,
    color: "warning",
    description: "Lentidão ou instabilidade",
  },
  {
    value: "documentacao",
    label: "Documentação",
    icon: MenuBookOutlined,
    color: "info",
    description: "Faltam explicações",
  },
  {
    value: "seguranca",
    label: "Segurança",
    icon: SecurityOutlined,
    color: "error",
    description: "Risco ou vulnerabilidade",
  },
  {
    value: "outro",
    label: "Outro",
    icon: HelpOutlineOutlined,
    color: "default",
    description: "Outras sugestões",
  },
];

const COLOR_MAP = {
  error: { main: "#f44336", light: "rgba(244, 67, 54, 0.12)", border: "rgba(244, 67, 54, 0.5)" },
  primary: { main: null, light: null, border: null }, // será resolvido com theme
  secondary: { main: "#9c27b0", light: "rgba(156, 39, 176, 0.12)", border: "rgba(156, 39, 176, 0.5)" },
  warning: { main: "#ff9800", light: "rgba(255, 152, 0, 0.12)", border: "rgba(255, 152, 0, 0.5)" },
  info: { main: "#2196f3", light: "rgba(33, 150, 243, 0.12)", border: "rgba(33, 150, 243, 0.5)" },
  default: { main: "#757575", light: "rgba(117, 117, 117, 0.12)", border: "rgba(117, 117, 117, 0.4)" },
};

export default function StyledSuggestionRatingComponent({
  setRating = () => {},
  questionId = null,
  rating = [],
  readOnly = false,
}) {
  const theme = useTheme();

  // injeta as cores do primary no mapa
  const resolvedColorMap = {
    ...COLOR_MAP,
    primary: {
      main: theme.palette.primary.main,
      light: alpha(theme.palette.primary.main, 0.12),
      border: alpha(theme.palette.primary.main, 0.5),
    },
  };

  const valorSelecionado =
    rating?.find((r) => r.questionId === questionId)?.answer ?? null;

  const handleSelect = (value) => {
    if (readOnly) return;
    setRating((prev) => {
      const jaExiste = prev?.find((r) => r?.questionId === questionId);
      if (jaExiste) {
        // toggle off se clicar no mesmo
        if (jaExiste.answer === value) {
          return prev.filter((r) => r.questionId !== questionId);
        }
        return prev.map((r) =>
          r.questionId === questionId ? { questionId, answer: value } : r
        );
      }
      return [...prev, { questionId, answer: value }];
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        width: "100%",
      }}
    >
      {CATEGORIES.map((cat) => {
        const Icon = cat.icon;
        const isSelected = valorSelecionado === cat.value;
        const colors = resolvedColorMap[cat.color] || resolvedColorMap.default;

        return (
          <Box
            key={cat.value}
            onClick={() => handleSelect(cat.value)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 1.2,
              borderRadius: "14px",
              border: `1.5px solid`,
              borderColor: isSelected ? colors.border : (
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(0,0,0,0.12)"
              ),
              backgroundColor: isSelected
                ? colors.light
                : "transparent",
              cursor: readOnly ? "default" : "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: isSelected
                ? `0 0 0 3px ${alpha(colors.main, 0.15)}, 0 2px 8px ${alpha(colors.main, 0.2)}`
                : "none",
              "&:hover": readOnly
                ? {}
                : {
                    backgroundColor: colors.light,
                    transform: "translateY(-1px)",
                  },
              userSelect: "none",
            }}
          >
            <Icon
              sx={{
                fontSize: "18px",
                color: isSelected ? colors.main : theme.palette.text.secondary,
                transition: "color 0.2s ease",
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? colors.main : theme.palette.text.primary,
                  lineHeight: 1.2,
                  transition: "color 0.2s ease",
                }}
              >
                {cat.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: theme.palette.text.secondary,
                  lineHeight: 1.2,
                }}
              >
                {cat.description}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
